import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Iter 139 — ClientSchedule
 * Pagina pubblica MINIMALE che consente al cliente finale di prenotare lo slot
 * del sopralluogo per una pratica già pagata (via bonifico e sbloccata da
 * Admin/Accounting). Nessuna scelta di pagamento, nessuna email obbligatoria:
 * solo il calendario. Il link è inviato per email dal backend all'atto della
 * conferma pagamento.
 *
 * URL: `/schedule/:pid`
 * Riusa gli endpoint pubblici già esistenti:
 *  - `GET /public/checkout/{pid}` per l'header pratica + `has_slot`
 *  - `GET /public/checkout/{pid}/slots?year&month` per gli slot mensili
 *  - `POST /public/checkout/{pid}/book-slot` per prenotare.
 */
export default function ClientSchedule() {
  const { pid } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slotsData, setSlotsData] = useState({ slots: [] });
  const [selectedDate, setSelectedDate] = useState(null);
  const [chosenSlot, setChosenSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [bookedSlot, setBookedSlot] = useState(null);
  const [slotMonth, setSlotMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/checkout/${pid}`)
      .then(r => setInfo(r.data))
      .catch(err => setError(err?.response?.data?.detail || "Link non valido"))
      .finally(() => setLoading(false));
  }, [pid]);

  useEffect(() => {
    if (!info) return;
    axios.get(`${API}/public/checkout/${pid}/slots`, { params: { year: slotMonth.year, month: slotMonth.month } })
      .then(r => setSlotsData(r.data))
      .catch(() => setSlotsData({ slots: [] }));
  }, [info, pid, slotMonth]);

  const daysWithSlots = useMemo(() => {
    const set = new Set();
    (slotsData.slots || []).forEach(s => set.add(s.date));
    return set;
  }, [slotsData]);

  const slotsForDate = useMemo(() => {
    if (!selectedDate) return [];
    return (slotsData.slots || []).filter(s => s.date === selectedDate);
  }, [slotsData, selectedDate]);

  const bookSlot = async () => {
    if (!chosenSlot) { toast.error("Seleziona uno slot"); return; }
    setBooking(true);
    try {
      const r = await axios.post(`${API}/public/checkout/${pid}/book-slot`, {
        slot_date: chosenSlot.date,
        slot_hour: chosenSlot.hour,
        slot_minute: chosenSlot.minute || 0,
        slot_operator_id: chosenSlot.operator_id,
      });
      setBookedSlot(r.data);
      toast.success("Sopralluogo prenotato!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore prenotazione slot");
    } finally { setBooking(false); }
  };

  if (loading) return <div className="p-6 text-center">Caricamento…</div>;
  if (error) return <div className="p-6 text-center text-red-600">{error}</div>;
  if (!info) return null;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-lg border shadow-sm p-6 space-y-6" data-testid="client-schedule-page">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Prenota il sopralluogo</h1>
          <p className="text-sm text-slate-600 mt-1">
            Servizio: <strong>{info.service?.name || info.service_name}</strong>
          </p>
          {info.property_address && (
            <p className="text-xs text-slate-500">Immobile: {info.property_address}</p>
          )}
        </div>

        {bookedSlot ? (
          <div
            className="rounded-lg border-2 border-emerald-400 bg-emerald-50 p-5 text-center space-y-2"
            data-testid="schedule-booked"
          >
            <div className="text-3xl">✅</div>
            <div className="text-base font-bold text-emerald-900">Sopralluogo prenotato</div>
            <div className="text-sm text-slate-700">
              {bookedSlot.slot_date} · {String(bookedSlot.slot_hour).padStart(2, "0")}:{String(bookedSlot.slot_minute || 0).padStart(2, "0")}
            </div>
            <div className="text-xs text-slate-600">Riceverai una email di conferma con i dettagli.</div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setSlotMonth(prev => {
                  const m = prev.month - 1;
                  return m < 1 ? { year: prev.year - 1, month: 12 } : { ...prev, month: m };
                })}
                className="text-sm text-indigo-700 hover:underline"
              >← Mese prec.</button>
              <div className="text-sm font-semibold">
                {new Date(slotMonth.year, slotMonth.month - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
              </div>
              <button
                type="button"
                onClick={() => setSlotMonth(prev => {
                  const m = prev.month + 1;
                  return m > 12 ? { year: prev.year + 1, month: 1 } : { ...prev, month: m };
                })}
                className="text-sm text-indigo-700 hover:underline"
              >Mese succ. →</button>
            </div>

            <div className="text-xs text-slate-500 text-center">
              {daysWithSlots.size > 0 ? `${daysWithSlots.size} giorni con slot disponibili` : "Nessuno slot disponibile in questo mese"}
            </div>

            <div className="flex flex-wrap gap-2">
              {Array.from(daysWithSlots).sort().map(date => (
                <button
                  key={date}
                  type="button"
                  onClick={() => { setSelectedDate(date); setChosenSlot(null); }}
                  className={`px-3 py-2 rounded-md text-sm border transition ${
                    selectedDate === date
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-slate-800 border-slate-300 hover:border-indigo-400"
                  }`}
                  data-testid={`schedule-day-${date}`}
                >
                  {date.slice(-2)}/{date.slice(5, 7)}
                </button>
              ))}
            </div>

            {selectedDate && slotsForDate.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-700">Slot disponibili il {selectedDate}</div>
                <div className="flex flex-wrap gap-2">
                  {slotsForDate.map((s, i) => (
                    <button
                      key={`${s.date}-${s.hour}-${s.minute}-${s.operator_id}-${i}`}
                      type="button"
                      onClick={() => setChosenSlot(s)}
                      className={`px-3 py-2 rounded-md text-sm border transition ${
                        chosenSlot === s
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-slate-800 border-slate-300 hover:border-emerald-400"
                      }`}
                      data-testid={`schedule-slot-${s.hour}-${s.minute}`}
                    >
                      {String(s.hour).padStart(2, "0")}:{String(s.minute || 0).padStart(2, "0")}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {chosenSlot && (
              <Button
                onClick={bookSlot}
                disabled={booking}
                className="w-full ht-gradient border-0 py-5 text-base font-semibold"
                data-testid="schedule-book-btn"
              >
                {booking ? "Prenotazione…" : "Conferma sopralluogo"}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
